import { Moment } from 'moment'

import { DefaultFile } from 'Core/interfaces'
import { StateProvince } from 'Core/utils/storeHelpers/types'

import { AdditionalInfoFormControls } from 'Portals/Supplier/Pages/RegistrationDocument/Body/Form/AdditionalInfoFormControls/types'
import { AddressFormControls } from 'Portals/Supplier/Pages/RegistrationDocument/Body/Form/AddressFormControls/types'
import { CommitmentFormControls } from 'Portals/Supplier/Pages/RegistrationDocument/Body/Form/CommitmentFormControls/types'
import { FinancialFormControls } from 'Portals/Supplier/Pages/RegistrationDocument/Body/Form/FinancialFormControls/types'
import { MainFormControls } from 'Portals/Supplier/Pages/RegistrationDocument/Body/Form/MainFormControls/types'

import { ProgramFormControls } from '../Pages/RegistrationDocument/Body/Form/ProgramFormControls/types'

export enum RegistrationDocumentAdditionalLocationFormFields {
	id = 'id',
	locationName = 'locationName',
	siteContactName = 'siteContactName',
	siteContactPhoneNumber = 'siteContactPhoneNumber',
	addressLine1 = 'addressLine1',
	addressLine2 = 'addressLine2',
	stateProvinceId = 'stateProvinceId',
	city = 'city',
	zipCode = 'zipCode',
	countryId = 'countryId',
	countyId = 'countyId',
	remitAddressLine1 = 'remitAddressLine1',
	remitAddressLine2 = 'remitAddressLine2',
	remitStateProvinceId = 'remitStateProvinceId',
	remitCity = 'remitCity',
	remitZipCode = 'remitZipCode',
	remitCountryId = 'remitCountryId',
	remitCountyId = 'remitCountyId',
	remitEmailAddress = 'remitEmailAddress',
	numberOfEmployeesFrom = 'numberOfEmployeesFrom',
	numberOfEmployeesTo = 'numberOfEmployeesTo',
	epaId = 'epaId',
	usDotNumber = 'usDotNumber',
	w9EinNumber = 'w9EinNumber',
	employeeRangeId = 'employeeRangeId'
}

export enum RegistrationDocumentAdditionalLocationFields {
	id = 'id',
	locationName = 'locationName',
	siteContactName = 'siteContactName',
	siteContactPhoneNumber = 'siteContactPhoneNumber',
	addressLine1 = 'addressLine1',
	addressLine2 = 'addressLine2',
	stateProvince = 'stateProvince',
	city = 'city',
	zipCode = 'zipCode'
}

export enum DocumentTypeLabels {
	w9 = 'W9',
	proofOfInsurance = 'Proof of Insurance',
	certificateOfInsurance = 'Certificate of Insurance',
	contract = 'Contract',
	nda = 'NDA',
	transportationContingencySpillPlan = 'Transportation Contingency Spill Plan',
	CSRSustainabilityPrograms = 'CSR/Sustainability Programs',
	safety = 'Safety',
	other = 'Other'
}

export enum RegistrationDocumentDocumentFields {
	id = 'id',
	documentType = 'documentType',
	documentName = 'documentName',
	uploadedBy = 'uploadedBy',
	uploadDate = 'uploadDate',
	isRequired = 'isRequired'
}

export enum RegistrationDocumentUploadDocumentFormFields {
	documentTypeId = 'documentTypeId',
	file = 'file'
}

export declare namespace RegistrationDocument {
	interface SupplierRequest {
		id: number
		supplierName: string
		supplierPhoneNumber: string
		supplierEmailAddress: string
		supplierWebsiteAddress: string
		serviceScopes: Entity[]
		acceptedSuppliesAndServices: string

		addressLine1: string
		addressLine2: string
		stateProvince: StateProvince
		city: string
		zipCode: string
		county: Entity

		psLegalName: string
		psDiversity: Entity
		psRemittanceAddress1: string
		psRemittanceAddress2: string
		psStateProvince: StateProvince
		psCity: string
		psZipCode: string
		psCounty: Entity

		dbaCompanyName: string
		yearCompanyEstablished: number
		numberOfEmployeesFrom: number
		numberOfEmployeesTo: number
		legalStructure: Entity
		legalStructureExplanation: BasicString
		epaId: string
		transporterEpaId: string
		transporterDotLicenceNumber: string

		qualityCertificationsAvailable: boolean
		qualityCertifications: Entity[]
		qualityCertExplanationOfOther: string
		environmentalManagementSystem: string
		emrRateEnabled: boolean
		emrEmptyReason: Entity
		emrEmptyOtherReason: string
		oshaRateEnabled: boolean
		oshaEmptyReason: Entity
		oshaEmptyOtherReason: string
		emrOshaRates: Rate[]
		employmentPolicies: Entity[]
		companyReceivedFines: boolean
		issuesAddressesByCompanySafetyProgram: Entity[]
		processOfAddressingComplaints: string
		primaryWayOfSatisfactionLevelDetermination: string

		csrSustainabilityPrograms: Entity[]
		otherReportingAssessments: string
		engagedInSupplyChainTopics: boolean
		ethicalPolicies: Entity[]

		supplierRequestStatus: Entity

		codeOfConductAcceptance: BasicString
	}

	interface Rate {
		id: number
		emrRate: BasicString
		oshaRate: BasicString
		lostTimeRate: BasicString
		fatalities: BasicString
		year: number
	}

	interface Form {
		isRequiredSchema: boolean
		[MainFormControls.supplierName]: BasicString
		[MainFormControls.supplierPhoneNumber]: BasicString
		[MainFormControls.supplierEmailAddress]: BasicString
		[MainFormControls.supplierWebsiteAddress]: BasicString
		[MainFormControls.serviceScopes]: LabelValue<number>[] | null
		[MainFormControls.acceptedSuppliesAndServices]: BasicString

		[AddressFormControls.addressLine1]: BasicString
		[AddressFormControls.addressLine2]: BasicString
		[AddressFormControls.stateProvince]: LabelValueExtra | null
		[AddressFormControls.city]: BasicString
		[AddressFormControls.zipCode]: BasicString
		[AddressFormControls.county]: LabelValue<number> | null

		[AdditionalInfoFormControls.dbaCompanyName]: BasicString
		[AdditionalInfoFormControls.yearCompanyEstablished]: Moment | null
		[AdditionalInfoFormControls.numberEmployeesCompanywide]: LabelValue<number> | null
		[AdditionalInfoFormControls.legalStructure]: LabelValue<number> | null
		[AdditionalInfoFormControls.otherExplanationLegalStructure]: BasicString
		[AdditionalInfoFormControls.epaId]: BasicString
		[AdditionalInfoFormControls.transporterEpaId]: BasicString
		[AdditionalInfoFormControls.transporterDOTLicenseNumber]: BasicString

		[FinancialFormControls.legalNameSupplierParentCompany]: BasicString
		[FinancialFormControls.diversityChoices]: LabelValue<number> | null
		[FinancialFormControls.addressLine1]: BasicString
		[FinancialFormControls.addressLine2]: BasicString
		[FinancialFormControls.stateProvince]: LabelValueExtra | null
		[FinancialFormControls.city]: BasicString
		[FinancialFormControls.zipCode]: BasicString
		[FinancialFormControls.county]: LabelValue<number> | null

		[CommitmentFormControls.isCertifications]: boolean
		[CommitmentFormControls.certificationsProvideToValidate]: Nullable<LabelValue<number>[]>
		[CommitmentFormControls.otherExplanationOfCertifications]: BasicString
		[CommitmentFormControls.environmentalManagementSystem]: BasicString
		[CommitmentFormControls.isOshaRate]: boolean
		[CommitmentFormControls.reasonOSHA]: Nullable<LabelValue<number>>
		[CommitmentFormControls.otherExplanationOfOSHA]: BasicString
		[CommitmentFormControls.isEmrRate]: boolean
		[CommitmentFormControls.reasonEmr]: Nullable<LabelValue<number>>
		[CommitmentFormControls.otherExplanationOfEMR]: BasicString
		[CommitmentFormControls.isCompanyReceivedViolationsOrFines]: boolean
		[CommitmentFormControls.employmentPolicies]: Nullable<LabelValue<number>[]>
		[CommitmentFormControls.issuesAddressed]: Nullable<LabelValue<number>[]>
		[CommitmentFormControls.processForAddressingComplaints]: BasicString
		[CommitmentFormControls.wayToDetermineCustomersSatisfaction]: BasicString
		[CommitmentFormControls.rates]: Rate[]

		[ProgramFormControls.csrSustainabilityPrograms]: Nullable<LabelValue<number>[]>
		[ProgramFormControls.otherReportingAssessments]: BasicString
		[ProgramFormControls.ethicalPoliciesCommunications]: Nullable<LabelValue<number>[]>
		[ProgramFormControls.doYouEngageSupplyChain]: boolean
	}

	interface Request {
		supplierName: BasicString
		supplierPhoneNumber: BasicString
		supplierWebsiteAddress: BasicString
		supplierEmailAddress: BasicString
		acceptedSuppliesAndServices: BasicString
		serviceScopes: Nullable<number[]>

		addressLine1: BasicString
		addressLine2: BasicString
		stateProvinceId: BasicNumber
		city: BasicString
		zipCode: BasicString
		countyId: BasicNumber

		dbaCompanyName: BasicString
		yearCompanyEstablished: BasicNumber
		numberOfEmployeesFrom: BasicNumber
		numberOfEmployeesTo: BasicNumber
		legalStructureId: BasicNumber
		legalStructureExplanation: BasicString
		epaId: BasicString
		transporterEpaId: BasicString
		transporterDotLicenceNumber: BasicString

		psLegalName: BasicString
		psDiversityId: BasicNumber
		psRemittanceAddress1: BasicString
		psRemittanceAddress2: BasicString
		psStateProvinceId: BasicNumber
		psCity: BasicString
		psZipCode: BasicString
		psCountyId: BasicNumber

		qualityCertificationsAvailable: boolean
		qualityCertifications: Nullable<number[]>
		qualityCertExplanationOfOther: BasicString
		environmentalManagementSystem: BasicString
		employmentPolicies: Nullable<number[]>
		companyReceivedFines: boolean
		issuesAddressesByCompanySafetyProgram: Nullable<number[]>
		processOfAddressingComplaints: BasicString
		primaryWayOfSatisfactionLevelDetermination: BasicString
		emrRateEnabled: boolean
		emrEmptyReasonId: BasicNumber
		emrEmptyOtherReason: BasicString
		oshaRateEnabled: boolean
		oshaEmptyReasonId: BasicNumber
		oshaEmptyOtherReason: BasicString
		emrOshaRates: Omit<Rate, 'id'>[]

		csrSustainabilityPrograms: Nullable<number[]>
		otherReportingAssessments: BasicString
		ethicalPolicies: Nullable<number[]>
		engagedInSupplyChainTopics: boolean
	}

	namespace Document {
		interface Contact {
			id: number
			lastName: BasicString
			firstName: BasicString
		}
		interface Basic {
			[RegistrationDocumentDocumentFields.id]: number
			[RegistrationDocumentDocumentFields.documentType]: Entity | null
			[RegistrationDocumentDocumentFields.documentName]: BasicString
			[RegistrationDocumentDocumentFields.uploadDate]: BasicString
			[RegistrationDocumentDocumentFields.uploadedBy]: Contact | null
			[RegistrationDocumentDocumentFields.isRequired]: boolean
		}

		interface Form {
			[RegistrationDocumentUploadDocumentFormFields.documentTypeId]: BasicString
			[RegistrationDocumentUploadDocumentFormFields.file]: DefaultFile | null
		}

		interface Request {
			[RegistrationDocumentUploadDocumentFormFields.documentTypeId]: number
			[RegistrationDocumentUploadDocumentFormFields.file]: File | null
		}
	}

	namespace Document {
		interface Contact {
			id: number
			lastName: BasicString
			firstName: BasicString
		}
		interface Basic {
			[RegistrationDocumentDocumentFields.id]: number
			[RegistrationDocumentDocumentFields.documentType]: Entity | null
			[RegistrationDocumentDocumentFields.documentName]: BasicString
			[RegistrationDocumentDocumentFields.uploadDate]: BasicString
			[RegistrationDocumentDocumentFields.uploadedBy]: Contact | null
			[RegistrationDocumentDocumentFields.isRequired]: boolean
		}

		interface Form {
			[RegistrationDocumentUploadDocumentFormFields.documentTypeId]: BasicString
			[RegistrationDocumentUploadDocumentFormFields.file]: DefaultFile | null
		}

		interface Request {
			[RegistrationDocumentUploadDocumentFormFields.documentTypeId]: number
			[RegistrationDocumentUploadDocumentFormFields.file]: File | null
		}
	}

	namespace AdditionalLocation {
		interface Basic {
			[RegistrationDocumentAdditionalLocationFields.id]: number
			[RegistrationDocumentAdditionalLocationFields.locationName]: BasicString
			[RegistrationDocumentAdditionalLocationFields.siteContactName]: BasicString
			[RegistrationDocumentAdditionalLocationFields.siteContactPhoneNumber]: BasicString
			[RegistrationDocumentAdditionalLocationFields.addressLine1]: BasicString
			[RegistrationDocumentAdditionalLocationFields.addressLine2]: BasicString
			[RegistrationDocumentAdditionalLocationFields.stateProvince]: BasicString
			[RegistrationDocumentAdditionalLocationFields.city]: BasicString
			[RegistrationDocumentAdditionalLocationFields.zipCode]: BasicString
		}

		interface Detailed {
			[RegistrationDocumentAdditionalLocationFormFields.id]: number
			[RegistrationDocumentAdditionalLocationFormFields.locationName]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.siteContactName]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.siteContactPhoneNumber]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.addressLine1]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.addressLine2]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.stateProvinceId]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.city]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.zipCode]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.countryId]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.countyId]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.remitAddressLine1]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.remitAddressLine2]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.remitStateProvinceId]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.remitCity]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.remitZipCode]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.remitCountryId]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.remitCountyId]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.remitEmailAddress]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.numberOfEmployeesFrom]: BasicNumber
			[RegistrationDocumentAdditionalLocationFormFields.numberOfEmployeesTo]: BasicNumber
			[RegistrationDocumentAdditionalLocationFormFields.epaId]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.usDotNumber]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.w9EinNumber]: BasicString
			[RegistrationDocumentAdditionalLocationFormFields.employeeRangeId]: BasicNumber
		}
		namespace Form {
			interface Basic {
				[RegistrationDocumentAdditionalLocationFormFields.locationName]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.siteContactName]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.siteContactPhoneNumber]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.addressLine1]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.addressLine2]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.stateProvinceId]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.city]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.zipCode]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.countryId]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.countyId]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.remitAddressLine1]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.remitAddressLine2]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.remitStateProvinceId]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.remitCity]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.remitZipCode]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.remitCountryId]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.remitCountyId]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.remitEmailAddress]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.numberOfEmployeesFrom]: BasicNumber
				[RegistrationDocumentAdditionalLocationFormFields.numberOfEmployeesTo]: BasicNumber
				[RegistrationDocumentAdditionalLocationFormFields.epaId]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.usDotNumber]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.w9EinNumber]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.employeeRangeId]?: BasicNumber
			}

			interface Request {
				[RegistrationDocumentAdditionalLocationFormFields.locationName]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.siteContactName]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.siteContactPhoneNumber]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.addressLine1]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.addressLine2]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.stateProvinceId]: BasicNumber
				[RegistrationDocumentAdditionalLocationFormFields.city]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.zipCode]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.countryId]: BasicNumber
				[RegistrationDocumentAdditionalLocationFormFields.countyId]: BasicNumber
				[RegistrationDocumentAdditionalLocationFormFields.remitAddressLine1]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.remitAddressLine2]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.remitStateProvinceId]: BasicNumber
				[RegistrationDocumentAdditionalLocationFormFields.remitCity]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.remitZipCode]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.remitCountryId]: BasicNumber
				[RegistrationDocumentAdditionalLocationFormFields.remitCountyId]: BasicNumber
				[RegistrationDocumentAdditionalLocationFormFields.remitEmailAddress]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.numberOfEmployeesFrom]: BasicNumber
				[RegistrationDocumentAdditionalLocationFormFields.numberOfEmployeesTo]: BasicNumber
				[RegistrationDocumentAdditionalLocationFormFields.epaId]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.usDotNumber]: BasicString
				[RegistrationDocumentAdditionalLocationFormFields.w9EinNumber]: BasicString
			}
		}
	}
}
